import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { ensureRecord, requirePermission } from '@/app/guards';
import { ReportPage, reportsQuery } from '@/features/reports';
import { NotFoundView } from '@/features/shell';
import { chartsOpenFrom, chartsParam } from '@/patterns';
import { i18next } from '@/shared/i18n';
import { currentPeriod } from '@/shared/lib/dates';

export const Route = createFileRoute('/_app/reports/$reportKey')({
  validateSearch: z.object({
    period: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    asAt: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .catch(undefined),
    charts: z.enum(['show', 'hide']).optional().catch(undefined),
  }),
  staticData: { crumb: 'nav.reports', crumbTo: '/reports' },
  beforeLoad: ({ context }) => requirePermission(context.queryClient, 'report.view', 'Reports'),
  loader: async ({ context, params }) => {
    const catalogue = await ensureRecord(context.queryClient.query({ ...reportsQuery, staleTime: 'static' }));
    const definition = catalogue.find((r) => r.key === params.reportKey);
    // Reports held by another screen (Performance, MIS, staff commission) open that screen.
    if (definition?.link) throw redirect({ to: definition.link as never });
    return { crumb: i18next.t(`defs.${params.reportKey}.name`, { ns: 'reports', defaultValue: definition?.name ?? params.reportKey }) };
  },
  notFoundComponent: function ReportNotFound() {
    const { reportKey } = Route.useParams();
    return <NotFoundView reference={reportKey} />;
  },
  component: function Report() {
    const { reportKey } = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const now = currentPeriod();
    return (
      <ReportPage
        reportKey={reportKey}
        params={{ period: search.period ?? now, asAt: search.asAt }}
        currentPeriod={now}
        chartsOpen={chartsOpenFrom(search.charts)}
        onChartsOpen={(open) => void navigate({ search: (prev) => ({ ...prev, charts: chartsParam(open) }), replace: true })}
        onChange={(patch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })}
      />
    );
  },
});
